import React, { useState, useEffect } from 'react';
import { clinicService } from '../../services/api';

const DoctorsSection = ({ clinicId }) => {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDoctorForm, setShowAddDoctorForm] = useState(false);
  const [newDoctor, setNewDoctor] = useState({
    primary_name: '',
    gender: '',
    speciality: '',
    service_provided: '',
    type: 'doctor'
  });
  const [editingDoctor, setEditingDoctor] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Gender options
  const genderOptions = [
    { value: 'male', label: 'Male' },
    { value: 'female', label: 'Female' },
    { value: 'other', label: 'Other' }
  ];

  // Type options
  const typeOptions = [
    { value: 'doctor', label: 'Doctor' },
    { value: 'clinic', label: 'Clinic' },
    { value: 'facility', label: 'Facility' }
  ];

  // Load doctors when component mounts
  useEffect(() => {
    const loadDoctors = async () => {
      try {
        setLoading(true);
        const data = await clinicService.getClinicDoctors(clinicId);
        setDoctors(data.doctors || []);
      } catch (err) {
        console.error('Error loading doctors:', err);
        setError('Failed to load doctors. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadDoctors();
  }, [clinicId]);

  const handleNewDoctorChange = (e) => {
    const { name, value } = e.target;
    setNewDoctor(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleAddDoctor = async () => {
    // Validate required fields
    if (!newDoctor.primary_name || !newDoctor.speciality || !newDoctor.service_provided) {
      setError('Please fill in all required fields.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      const data = await clinicService.addClinicDoctor(clinicId, newDoctor);
      setDoctors(prev => [...prev, data.doctor]);
      setNewDoctor({
        primary_name: '',
        gender: '',
        speciality: '',
        service_provided: '',
        type: 'doctor'
      });
      setShowAddDoctorForm(false);
      setSuccessMessage('Doctor added successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error adding doctor:', err);
      setError('Failed to add doctor. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditDoctor = (doctor) => {
    setEditingDoctor({
      ...doctor,
      gender: doctor.gender || '',
      type: doctor.type || 'doctor'
    });
  };

  const handleEditDoctorChange = (e) => {
    const { name, value } = e.target;
    setEditingDoctor(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdateDoctor = async () => {
    // Validate required fields
    if (!editingDoctor.primary_name || !editingDoctor.speciality || !editingDoctor.service_provided) {
      setError('Please fill in all required fields.');
      return;
    }

    try {
      setIsSaving(true);
      setError(null);
      const data = await clinicService.updateClinicDoctor(
        clinicId,
        editingDoctor.id,
        {
          primary_name: editingDoctor.primary_name,
          gender: editingDoctor.gender,
          speciality: editingDoctor.speciality,
          service_provided: editingDoctor.service_provided,
          type: editingDoctor.type
        }
      );
      setDoctors(prev => prev.map(doc => doc.id === editingDoctor.id ? data.doctor : doc));
      setEditingDoctor(null);
      setSuccessMessage('Doctor updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error updating doctor:', err);
      setError('Failed to update doctor. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDoctor = async (doctorId) => {
    if (!window.confirm('Are you sure you want to delete this doctor?')) {
      return;
    }

    try {
      setIsSaving(true);
      await clinicService.deleteClinicDoctor(clinicId, doctorId);
      setDoctors(prev => prev.filter(doc => doc.id !== doctorId));
      setSuccessMessage('Doctor deleted successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting doctor:', err);
      setError('Failed to delete doctor. Please try again later.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingDoctor(null);
    setError(null);
  };

  if (loading && doctors.length === 0) {
    return (
      <div className="flex justify-center p-8">
        <p className="text-gray-500">Loading doctors...</p>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-blue-700">
        <div className="flex items-start">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="font-medium">Module Requirement</p>
            <p className="text-sm">
              For full functionality, this feature works best with the <span className="font-bold">Structured Data</span> module enabled.
              When this module is active, doctors' information can be directly accessed by the AI assistant.
            </p>
          </div>
        </div>
      </div>

      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-700">Clinic Doctors</h3>
        <button
          onClick={() => setShowAddDoctorForm(!showAddDoctorForm)}
          className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
        >
          {showAddDoctorForm ? (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
              Hide Form
            </>
          ) : (
            <>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add New Doctor
            </>
          )}
        </button>
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Add New Doctor Form */}
      {showAddDoctorForm && (
        <div className="mb-6 p-4 border rounded bg-gray-50">
          <h4 className="mb-3 text-gray-700 font-medium">Add New Doctor</h4>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                name="primary_name"
                value={newDoctor.primary_name}
                onChange={handleNewDoctorChange}
                className="w-full p-2 border rounded"
                placeholder="Doctor's name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
              <select
                name="gender"
                value={newDoctor.gender}
                onChange={handleNewDoctorChange}
                className="w-full p-2 border rounded"
              >
                <option value="">Select Gender</option>
                {genderOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Specialty *</label>
              <input
                type="text"
                name="speciality"
                value={newDoctor.speciality}
                onChange={handleNewDoctorChange}
                className="w-full p-2 border rounded"
                placeholder="e.g. Cardiologist, Dermatologist"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                name="type"
                value={newDoctor.type}
                onChange={handleNewDoctorChange}
                className="w-full p-2 border rounded"
              >
                {typeOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">Services Provided *</label>
            <textarea
              name="service_provided"
              value={newDoctor.service_provided}
              onChange={handleNewDoctorChange}
              className="w-full p-2 border rounded"
              rows="3"
              placeholder="List of services provided by this doctor"
            ></textarea>
          </div>

          <div className="flex space-x-2">
            <button
              onClick={handleAddDoctor}
              disabled={isSaving}
              className={`px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {isSaving ? 'Adding...' : 'Add Doctor'}
            </button>
            <button
              onClick={() => {
                setShowAddDoctorForm(false);
                setNewDoctor({
                  primary_name: '',
                  gender: '',
                  speciality: '',
                  service_provided: '',
                  type: 'doctor'
                });
                setError(null);
              }}
              className="px-4 py-2 border rounded text-gray-600 hover:bg-gray-100"
              disabled={isSaving}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Doctors List */}
      {doctors.length === 0 ? (
        <div className="text-center p-8 bg-gray-50 rounded">
          <p className="text-gray-500">No doctors found for this clinic.</p>
          <p className="text-gray-500 mt-2">
            Add doctors to provide information about healthcare providers at this clinic.
          </p>
        </div>
      ) : (
        <>
          {/* Desktop table view */}
          <div className="hidden md:block overflow-x-auto">
            <table className="min-w-full bg-white border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 border text-left">Name</th>
                  <th className="py-2 px-4 border text-left">Gender</th>
                  <th className="py-2 px-4 border text-left">Speciality</th>
                  <th className="py-2 px-4 border text-left">Type</th>
                  <th className="py-2 px-4 border text-left">Services Provided</th>
                  <th className="py-2 px-4 border text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {doctors.map((doctor) => (
                  <tr key={doctor.id} className="border-t">
                    {editingDoctor && editingDoctor.id === doctor.id ? (
                      // Edit doctor row
                      <>
                        <td className="py-2 px-4 border" colSpan="6">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                              <input
                                type="text"
                                name="primary_name"
                                value={editingDoctor.primary_name}
                                onChange={handleEditDoctorChange}
                                className="w-full p-2 border rounded"
                                placeholder="Doctor's name"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                              <select
                                name="gender"
                                value={editingDoctor.gender}
                                onChange={handleEditDoctorChange}
                                className="w-full p-2 border rounded"
                              >
                                <option value="">Select Gender</option>
                                {genderOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Speciality *</label>
                              <input
                                type="text"
                                name="speciality"
                                value={editingDoctor.speciality}
                                onChange={handleEditDoctorChange}
                                className="w-full p-2 border rounded"
                                placeholder="e.g., Cardiology, Pediatrics"
                              />
                            </div>

                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                              <select
                                name="type"
                                value={editingDoctor.type}
                                onChange={handleEditDoctorChange}
                                className="w-full p-2 border rounded"
                              >
                                {typeOptions.map(option => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </div>

                            <div className="col-span-2">
                              <label className="block text-sm font-medium text-gray-700 mb-1">Services Provided *</label>
                              <textarea
                                name="service_provided"
                                value={editingDoctor.service_provided}
                                onChange={handleEditDoctorChange}
                                className="w-full p-2 border rounded"
                                rows="3"
                                placeholder="Enter services provided by the doctor"
                              ></textarea>
                            </div>

                            <div className="col-span-2 flex space-x-2 pt-3">
                              <button
                                onClick={handleUpdateDoctor}
                                disabled={isSaving}
                                className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
                              >
                                {isSaving ? 'Saving...' : 'Save Changes'}
                              </button>
                              <button
                                onClick={handleCancelEdit}
                                className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </td>
                      </>
                    ) : (
                      // View doctor row
                      <>
                        <td className="py-2 px-4 border">
                          {doctor.primary_name}
                        </td>
                        <td className="py-2 px-4 border">
                          {doctor.gender ? doctor.gender.charAt(0).toUpperCase() + doctor.gender.slice(1) : '-'}
                        </td>
                        <td className="py-2 px-4 border">
                          {doctor.speciality}
                        </td>
                        <td className="py-2 px-4 border">
                          {doctor.type ? doctor.type.charAt(0).toUpperCase() + doctor.type.slice(1) : 'Doctor'}
                        </td>
                        <td className="py-2 px-4 border">
                          <div className="max-h-24 overflow-y-auto">
                            {doctor.service_provided}
                          </div>
                        </td>
                        <td className="py-2 px-4 border">
                          <div className="flex space-x-2">
                            <button
                              onClick={() => handleEditDoctor(doctor)}
                              className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleDeleteDoctor(doctor.id)}
                              className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile card view */}
          <div className="md:hidden space-y-4">
            {doctors.map((doctor) => (
              <div key={doctor.id} className="border rounded-lg p-4 bg-white shadow-sm">
                {editingDoctor && editingDoctor.id === doctor.id ? (
                  // Edit doctor card
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                      <input
                        type="text"
                        name="primary_name"
                        value={editingDoctor.primary_name}
                        onChange={handleEditDoctorChange}
                        className="w-full p-2 border rounded"
                        placeholder="Doctor's name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                      <select
                        name="gender"
                        value={editingDoctor.gender}
                        onChange={handleEditDoctorChange}
                        className="w-full p-2 border rounded"
                      >
                        <option value="">Select Gender</option>
                        {genderOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Speciality *</label>
                      <input
                        type="text"
                        name="speciality"
                        value={editingDoctor.speciality}
                        onChange={handleEditDoctorChange}
                        className="w-full p-2 border rounded"
                        placeholder="e.g., Cardiology, Pediatrics"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                      <select
                        name="type"
                        value={editingDoctor.type}
                        onChange={handleEditDoctorChange}
                        className="w-full p-2 border rounded"
                      >
                        {typeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Services Provided *</label>
                      <textarea
                        name="service_provided"
                        value={editingDoctor.service_provided}
                        onChange={handleEditDoctorChange}
                        className="w-full p-2 border rounded"
                        rows="3"
                        placeholder="Enter services provided by the doctor"
                      ></textarea>
                    </div>

                    <div className="flex space-x-2 pt-3">
                      <button
                        onClick={handleUpdateDoctor}
                        disabled={isSaving}
                        className="px-3 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
                      >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                      <button
                        onClick={handleCancelEdit}
                        className="px-3 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // View doctor card
                  <>
                    <div className="flex justify-between items-start">
                      <h3 className="font-medium text-lg text-gray-800">{doctor.primary_name}</h3>
                      <div className="flex space-x-2">
                        <button
                          onClick={() => handleEditDoctor(doctor)}
                          className="px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteDoctor(doctor.id)}
                          className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                        >
                          Delete
                        </button>
                      </div>
                    </div>

                    <div className="mt-3 space-y-2 text-sm">
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-gray-500">Gender:</div>
                        <div className="col-span-2">
                          {doctor.gender ? doctor.gender.charAt(0).toUpperCase() + doctor.gender.slice(1) : '-'}
                        </div>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-gray-500">Speciality:</div>
                        <div className="col-span-2">{doctor.speciality}</div>
                      </div>

                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-gray-500">Type:</div>
                        <div className="col-span-2">
                          {doctor.type ? doctor.type.charAt(0).toUpperCase() + doctor.type.slice(1) : 'Doctor'}
                        </div>
                      </div>

                      <div className="mt-3">
                        <div className="text-gray-500 mb-1">Services Provided:</div>
                        <div className="bg-gray-50 p-2 rounded text-xs">
                          {doctor.service_provided}
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default DoctorsSection;
